import { useCallback, useEffect, useMemo, useState } from 'react';
import { useUrlParam, useUrlPanelSync } from '../../../hooks/useUrlParam';
import { useSearchParams } from 'react-router-dom';
import type { Client } from '../../../context/ClientContext';
import { useUser } from '../../../context/UserContext';
import { AI_JOB_STATUS_LABEL, aiJobStatusTone } from '../../../lib/aiJobLabels';
import { contentQueueStatus } from '../../../lib/contentQueue';
import { CONTENT_TYPES, type ContentType } from '../../../lib/keywordSweep';
import {
  decideOffer,
  deleteOffer,
  enqueueOfferIdeation,
  fetchCompetencies,
  fetchOfferLivePages,
  fetchOfferPages,
  fetchOffers,
  OFFER_STATUS,
  saveOffer,
  type Competency,
  type Offer,
  type OfferFields,
  type OfferStatus,
} from '../../../lib/offers';
import { supabase } from '../../../lib/supabase';
import type { AiJobStatus } from '../../../lib/supabase.types';
import { Badge, Button, Card, Checkbox, Input, SegmentedControl, SidePanel, useToast } from '../../ui';
import { textareaStyle } from './offers/styles';

interface OffersSectionProps {
  client: Client;
  expandedSection: string | null;
  toggleSection: (section: string) => void;
}

type OfferPage = Awaited<ReturnType<typeof fetchOfferPages>>[number];
type LivePage = Awaited<ReturnType<typeof fetchOfferLivePages>>[number];
type IdeationJob = { id: string; status: AiJobStatus; updated_at: string };

const faint: React.CSSProperties = { color: 'var(--text-faint)' };
const pathOf = (url: string) => {
  try {
    return new URL(url).pathname;
  } catch {
    return url;
  }
};
const OPEN_JOB: AiJobStatus[] = ['spec_queued', 'spec_in_progress', 'spec_revision'];
const EMPTY_FIELDS: OfferFields = { name: '', audience: '', outcome: '', mechanism: '', cta: '', competency_ids: [], include_ideas: '', exclude_ideas: '' };

const empty = (text: React.ReactNode) => (
  <div style={{ padding: '24px 0', textAlign: 'center', fontSize: 'var(--text-sm)', color: 'var(--text-muted)', lineHeight: 1.6 }}>{text}</div>
);

/**
 * Client detail → Offers: what the client sells, built from its competencies (Knowledge) for demand that
 * exists. An agent ideates offers; a person approves (→ production) or denies each. A production offer
 * opens to its money pages, which are Content Queue rows tied to it. See src/lib/offers.ts.
 */
export default function OffersSection({ client, expandedSection }: OffersSectionProps) {
  const { user } = useUser();
  const toast = useToast();
  const [offers, setOffers] = useState<Offer[]>([]);
  const [competencies, setCompetencies] = useState<Competency[]>([]);
  const [pages, setPages] = useState<OfferPage[]>([]);
  const [livePages, setLivePages] = useState<LivePage[]>([]);
  const [job, setJob] = useState<IdeationJob | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [tab, setTab] = useState<OfferStatus | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [fields, setFields] = useState<OfferFields | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [denying, setDenying] = useState<Offer | null>(null);
  const [denyReason, setDenyReason] = useState('');
  const [ideateOpen, setIdeateOpen] = useState(false);
  const [steer, setSteer] = useState('');
  const [busy, setBusy] = useState(false);
  const isOpen = expandedSection === 'offers';
  const [, setSearchParams] = useSearchParams();

  const load = useCallback(async () => {
    try {
      const [o, c, p, lp, j] = await Promise.all([
        fetchOffers(client.id),
        fetchCompetencies(client.id),
        fetchOfferPages(client.id),
        fetchOfferLivePages(client.id),
        supabase
          .from('ai_jobs')
          .select('id, status, updated_at')
          .eq('kind', 'offers')
          .eq('brief->offer_ideation->>client_id', client.id)
          .order('created_at', { ascending: false })
          .limit(1),
      ]);
      setOffers(o);
      setCompetencies(c);
      setPages(p);
      setLivePages(lp);
      setJob((j.data?.[0] as IdeationJob | undefined) ?? null);
    } catch (err) {
      console.error('Error loading offers:', err);
    } finally {
      setIsLoading(false);
    }
  }, [client.id]);

  useEffect(() => {
    if (!isOpen) return;
    void load();
    const filter = `client_id=eq.${client.id}`;
    const channel = supabase
      .channel(`offers-${client.id}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'client_offers', filter }, () => void load())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'client_competencies', filter }, () => void load())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'client_content_queue', filter }, () => void load())
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [isOpen, client.id, load]);

  const counts = useMemo(() => {
    const c: Record<OfferStatus, number> = { ideated: 0, production: 0, denied: 0 };
    offers.forEach((o) => (c[o.status] += 1));
    return c;
  }, [offers]);
  // Ideated first when something is waiting on a decision; production otherwise.
  const shown: OfferStatus = tab ?? (counts.ideated ? 'ideated' : 'production');
  const list = offers.filter((o) => o.status === shown);
  const compName = useMemo(() => new Map(competencies.map((c) => [c.id, c.name])), [competencies]);
  const pagesOf = (offerId: string) => pages.filter((p) => p.offer_id === offerId);
  const liveOf = (offerId: string) => livePages.filter((p) => p.offer_id === offerId);
  const open = offers.find((o) => o.id === openId) ?? null;
  const ideating = !!job && OPEN_JOB.includes(job.status);

  // The open offer is ?offer=<id>; a new offer stays local.
  const [urlOffer, setUrlOffer] = useUrlParam('offer');
  useUrlPanelSync(
    urlOffer,
    fields && !isNew ? openId : null,
    !isLoading,
    (id) => {
      const o = offers.find((x) => x.id === id);
      if (!o) return;
      setIsNew(false);
      setOpenId(o.id);
      setFields({ name: o.name, audience: o.audience, outcome: o.outcome, mechanism: o.mechanism, cta: o.cta, competency_ids: o.competency_ids, include_ideas: o.include_ideas ?? '', exclude_ideas: o.exclude_ideas ?? '' });
    },
    () => {
      setOpenId(null);
      setIsNew(false);
      setFields(null);
    },
  );

  if (!isOpen) return null;

  const run = async (label: string, fn: () => Promise<unknown>) => {
    setBusy(true);
    try {
      await fn();
      await load();
      return true;
    } catch (err) {
      toast.error(label, err instanceof Error ? err.message : 'Nothing changed.');
      return false;
    } finally {
      setBusy(false);
    }
  };

  const decide = (offer: Offer, status: OfferStatus, reason?: string) =>
    user ? run('Couldn’t update the offer', () => decideOffer(offer.id, status, user.id, reason)) : Promise.resolve(false);

  const openOffer = (o: Offer) => setUrlOffer(o.id);
  const closeOffer = () => {
    setOpenId(null);
    setIsNew(false);
    setFields(null);
    if (urlOffer) setUrlOffer(null);
  };

  const saveFields = async () => {
    if (!fields) return;
    const ok = await run('Couldn’t save the offer', () => saveOffer(client.id, { ...fields, id: isNew ? undefined : (openId ?? undefined) }, user?.id));
    if (ok) {
      if (isNew) setTab('production');
      closeOffer();
    }
  };

  const ideate = async () => {
    if (!user) return;
    const ok = await run('Couldn’t queue the ideation', () => enqueueOfferIdeation({ client, note: steer, createdBy: { id: user.id, name: user.name || user.email } }));
    if (ok) {
      toast.success('Queued', 'Proposed offers land here as Ideated, ready to approve or deny.');
      setIdeateOpen(false);
    }
  };

  const showMoneyPages = () => setSearchParams({ section: 'content-queue', type: 'money' });

  const field = (label: string, key: keyof Omit<OfferFields, 'competency_ids'>, placeholder: string, multiline = false, rows = 2) =>
    fields ? (
      <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        <span className="t-eyebrow">{label}</span>
        {multiline ? (
          <textarea value={fields[key]} onChange={(e) => setFields({ ...fields, [key]: e.target.value })} rows={rows} placeholder={placeholder} style={textareaStyle} />
        ) : (
          <Input value={fields[key]} onChange={(e) => setFields({ ...fields, [key]: e.target.value })} placeholder={placeholder} />
        )}
      </label>
    ) : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)', minWidth: 0 }}>
      <Card
        eyebrow="Offers"
        title={isLoading ? 'Loading...' : `${counts.production} in production`}
        actions={
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'flex-end', alignItems: 'center' }}>
            {job ? (
              <span style={{ fontSize: 'var(--text-xs)', ...faint }}>
                Last ideation <Badge tone={aiJobStatusTone(job.status)}>{AI_JOB_STATUS_LABEL[job.status]}</Badge>
              </span>
            ) : null}
            <Button variant="secondary" size="sm" icon="plus" onClick={() => { setIsNew(true); setOpenId(null); setFields({ ...EMPTY_FIELDS }); }}>
              Add offer
            </Button>
            <Button variant="primary" size="sm" icon="sparkles" onClick={() => { setSteer(''); setIdeateOpen(true); }} disabled={ideating}>
              {ideating ? 'Ideating...' : 'Ideate offers'}
            </Button>
          </div>
        }
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: 'var(--space-3)' }}>
          <SegmentedControl
            value={shown}
            onChange={(id) => setTab(id as OfferStatus)}
            options={(['production', 'ideated', 'denied'] as OfferStatus[]).map((s) => ({ id: s, label: `${OFFER_STATUS[s].label} ${counts[s]}` }))}
          />
          <span style={{ flex: 1 }} />
          <Button variant="ghost" size="sm" icon="list-checks" onClick={showMoneyPages}>
            Money pages in the Content Queue
          </Button>
        </div>
        {isLoading ? null : list.length === 0 ? (
          empty(
            shown === 'ideated'
              ? competencies.length
                ? 'Nothing waiting. Ideate offers to have an agent propose some from the competencies and the demand we already see.'
                : `No competencies on file yet. Add them in Knowledge (or let the ideation fill what it can source), then ideate.`
              : shown === 'production'
                ? 'No production offers yet. Approve an ideated one, or add one by hand.'
                : 'Nothing denied.',
          )
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {list.map((o) => {
              const money = pagesOf(o.id);
              return (
                <div
                  key={o.id}
                  className="hover-wash"
                  onClick={() => openOffer(o)}
                  style={{ border: '1px solid var(--border-hairline)', borderRadius: 'var(--radius-sm)', padding: '10px 12px', cursor: 'pointer', display: 'flex', gap: '12px', alignItems: 'flex-start' }}
                >
                  <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--weight-medium)' as React.CSSProperties['fontWeight'], color: 'var(--text-body)' }}>{o.name}</span>
                      {o.source === 'agent' ? <Badge tone="info">agent</Badge> : null}
                      {o.status !== 'denied' ? (
                        <Badge tone={liveOf(o.id).length || money.length ? 'up' : 'warn'}>
                          {liveOf(o.id).length} live · {money.length} queued
                        </Badge>
                      ) : null}
                    </div>
                    {o.outcome || o.audience ? (
                      <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
                        {o.outcome}
                        {o.audience ? <span style={faint}> · for {o.audience}</span> : null}
                      </div>
                    ) : null}
                    <div style={{ fontSize: 'var(--text-2xs)', ...faint, display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                      {o.competency_ids.length ? <span>Built on {o.competency_ids.map((id) => compName.get(id) ?? '?').join(', ')}</span> : null}
                      {o.demand.length ? <span>Demand: {o.demand.slice(0, 3).map((d) => `“${d.query}”${d.volume ? ` ${d.volume}/mo` : ''}`).join(', ')}{o.demand.length > 3 ? ` +${o.demand.length - 3}` : ''}</span> : null}
                    </div>
                    {o.status === 'denied' && o.deny_reason ? <div style={{ fontSize: 'var(--text-xs)', color: 'var(--status-down)' }}>Denied: {o.deny_reason}</div> : null}
                  </div>
                  <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
                    {o.status === 'ideated' ? (
                      <>
                        <Button variant="ghost" size="sm" onClick={() => { setDenyReason(''); setDenying(o); }} disabled={busy}>
                          Deny
                        </Button>
                        <Button variant="primary" size="sm" icon="check" onClick={() => void decide(o, 'production')} disabled={busy}>
                          Approve
                        </Button>
                      </>
                    ) : o.status === 'denied' ? (
                      <Button variant="ghost" size="sm" icon="rotate-ccw" onClick={() => void decide(o, 'ideated')} disabled={busy}>
                        Reconsider
                      </Button>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* One offer: its fields, the competencies behind it, the demand that justified it, and its money pages. */}
      <SidePanel
        open={!!fields}
        onClose={closeOffer}
        width={680}
        eyebrow={isNew ? 'Offers · new' : open ? `Offers · ${OFFER_STATUS[open.status].label}` : 'Offers'}
        title={isNew ? 'Add an offer' : (open?.name ?? 'Offer')}
        footer={
          <div style={{ display: 'flex', gap: '8px' }}>
            {open && !isNew ? (
              <Button variant="danger" onClick={() => void run('Couldn’t delete the offer', () => deleteOffer(open.id)).then((ok) => ok && closeOffer())} disabled={busy}>
                Delete
              </Button>
            ) : null}
            <span style={{ flex: 1 }} />
            <Button variant="ghost" onClick={closeOffer} disabled={busy}>
              Cancel
            </Button>
            <Button variant="primary" onClick={saveFields} disabled={busy || !fields?.name.trim()}>
              {busy ? 'Saving...' : 'Save'}
            </Button>
          </div>
        }
      >
        {fields ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)', fontSize: 'var(--text-sm)' }}>
            {open?.rationale ? <div style={{ color: 'var(--text-muted)' }}>{open.rationale}</div> : null}
            {field('Offer', 'name', 'e.g. 24/7 emergency electrical repair')}
            {field('Who it’s for', 'audience', 'e.g. Chilliwack homeowners with no power or a burning smell')}
            {field('What they get', 'outcome', 'e.g. A licensed electrician on site within the hour, fixed the same night', true)}
            {field('How the business delivers it', 'mechanism', 'e.g. On-call crew, stocked vans, upfront call-out fee', true)}
            {field('What the page asks them to do', 'cta', 'e.g. Call now')}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
              <span style={faint}>Talking points for every page that sells this offer. Loose ideas, one per line: agents write each one in their own words for that page’s buyer and search.</span>
              {field('Ideas to include', 'include_ideas', 'e.g. You own the site and every account\nYou talk to the people building it\nFast on a phone, measured not promised', true, 4)}
              {field('Ideas to leave out', 'exclude_ideas', 'e.g. Free audits\nAnything about our process or philosophy before the buyer asks', true, 3)}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <span className="t-eyebrow">Built on</span>
              {competencies.length === 0 ? (
                <span style={faint}>No competencies on file. Add them in Knowledge.</span>
              ) : (
                competencies.map((c) => (
                  <Checkbox
                    key={c.id}
                    checked={fields.competency_ids.includes(c.id)}
                    onChange={(on) =>
                      setFields({ ...fields, competency_ids: on ? [...fields.competency_ids, c.id] : fields.competency_ids.filter((id) => id !== c.id) })
                    }
                    label={
                      <span>
                        {c.name} <span style={{ ...faint, fontSize: 'var(--text-xs)' }}>· {c.proof.length ? `${c.proof.length} proof` : 'no proof'}</span>
                      </span>
                    }
                  />
                ))
              )}
            </div>
            {open?.demand.length ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <span className="t-eyebrow">Demand behind it</span>
                {open.demand.map((d) => (
                  <span key={d.query} className="t-num" style={{ fontSize: 'var(--text-xs)' }}>
                    “{d.query}”
                    <span style={faint}>
                      {d.volume != null ? ` · ${d.volume}/mo` : ''}
                      {d.impressions != null ? ` · ${d.impressions} impr` : ''}
                      {d.position != null ? ` · pos ${Number(d.position).toFixed(1)}` : ''}
                      {d.source ? ` · ${d.source}` : ''}
                    </span>
                  </span>
                ))}
              </div>
            ) : null}
            {open && open.status !== 'denied' ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <span className="t-eyebrow">Live money pages · {liveOf(open.id).length}</span>
                {liveOf(open.id).length === 0 ? (
                  <span style={faint}>No live page sells this offer yet. Tie one from its page detail in Pages.</span>
                ) : (
                  liveOf(open.id).map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      className="hover-wash"
                      onClick={() => setSearchParams({ section: 'pages', page: p.id })}
                      style={{ all: 'unset', cursor: 'pointer', display: 'flex', alignItems: 'baseline', gap: '8px', padding: '2px 6px', margin: '0 -6px', borderRadius: 'var(--radius-sm)' }}
                    >
                      <span style={{ color: 'var(--text-body)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0, flex: 1 }}>{pathOf(p.url)}</span>
                      <span className="t-num" style={{ ...faint, fontSize: 'var(--text-xs)', whiteSpace: 'nowrap' }}>
                        {p.gsc_clicks ?? 0} clicks · {p.gsc_impressions ?? 0} impr{p.gsc_position != null ? ` · pos ${Number(p.gsc_position).toFixed(1)}` : ''}
                      </span>
                      {p.template_level ? <Badge tone="down">templated</Badge> : null}
                    </button>
                  ))
                )}
              </div>
            ) : null}
            {open && open.status !== 'denied' ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <span className="t-eyebrow">Queued money pages · {pagesOf(open.id).length}</span>
                {pagesOf(open.id).length === 0 ? (
                  <span style={faint}>None yet. Tie Content Queue rows to this offer from the queue’s edit panel.</span>
                ) : (
                  pagesOf(open.id).map((p) => {
                    const st = contentQueueStatus(p.status);
                    const type = CONTENT_TYPES[p.content_type as ContentType];
                    return (
                      <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        <span style={{ color: 'var(--text-body)' }}>{p.title}</span>
                        {p.target_keyword ? <span style={{ ...faint, fontSize: 'var(--text-xs)' }}>“{p.target_keyword}”{p.volume ? ` ${p.volume}/mo` : ''}</span> : null}
                        <span style={{ flex: 1 }} />
                        {type ? <Badge tone={type.tone}>{type.short}</Badge> : null}
                        <Badge tone={st.tone} dot>{st.label}</Badge>
                      </div>
                    );
                  })
                )}
              </div>
            ) : null}
          </div>
        ) : null}
      </SidePanel>

      <SidePanel
        open={!!denying}
        onClose={() => setDenying(null)}
        eyebrow="Deny offer"
        title={denying?.name ?? ''}
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
            <Button variant="ghost" onClick={() => setDenying(null)} disabled={busy}>
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={() => denying && void decide(denying, 'denied', denyReason).then((ok) => ok && setDenying(null))}
              disabled={busy || denyReason.trim().length < 3}
            >
              Deny
            </Button>
          </div>
        }
      >
        <label style={{ display: 'flex', flexDirection: 'column', gap: '6px', fontSize: 'var(--text-sm)' }}>
          <span className="t-eyebrow">Why not</span>
          <textarea value={denyReason} onChange={(e) => setDenyReason(e.target.value)} rows={4} style={textareaStyle} placeholder="The next ideation reads this and won’t propose it again." />
        </label>
      </SidePanel>

      <SidePanel
        open={ideateOpen}
        onClose={() => setIdeateOpen(false)}
        eyebrow="Offers"
        title="Ideate offers"
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
            <Button variant="ghost" onClick={() => setIdeateOpen(false)} disabled={busy}>
              Cancel
            </Button>
            <Button variant="primary" onClick={ideate} disabled={busy || !user}>
              {busy ? 'Queueing...' : 'Queue it'}
            </Button>
          </div>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)', fontSize: 'var(--text-sm)' }}>
          <p style={{ margin: 0, color: 'var(--text-muted)' }}>
            An agent reads {client.name}’s knowledge base, competencies, site and search data, fills in competencies it can prove, and proposes offers that
            real demand backs. They land here as Ideated for you to approve or deny. Denied offers are never proposed again.
          </p>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <span className="t-eyebrow">Steer</span>
            <textarea value={steer} onChange={(e) => setSteer(e.target.value)} rows={4} style={textareaStyle} placeholder="Optional. e.g. focus on commercial clients; they no longer do residential." />
          </label>
        </div>
      </SidePanel>
    </div>
  );
}
